import { initApolloClient } from '@drycut/apollo';
import { GetStoreDocument } from '@drycut/graphql';
import { MobileNavbar, PromotionBadge, Stack } from '@drycut/ui-components';
import { useEffect, useLayoutEffect } from 'react';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';

import { PassBubble } from '../PassBubble';
import { CheckoutIntroText } from './CheckoutIntroText';
import { CheckoutStep } from './CheckoutStep';
import { useCheckoutStore } from './checkoutStore';
import { CustomerDetailsHeading, PaymentHeading } from './headings';
import { CustomerDetailsStep, PaymentStep, SalonStep, ScheduleStep } from './steps';

type BookFormValues = {
  couponCode: string;
  existingCustomer: boolean;
  kidCustomer: boolean;
  name: string;
  phone: string;
  silentBooking: boolean;
};

export type BookingCheckoutProps = {
  manual?: boolean;
  storeSlug?: string;
};

export function BookingCheckout({
  manual = false,
  storeSlug,
}: BookingCheckoutProps): React.ReactElement {
  const defaultValues: BookFormValues = {
    phone: '',
    name: '',
    existingCustomer: false,
    kidCustomer: false,
    silentBooking: false,
    couponCode: '', // legacy, we might remove this in the future to use the new zustand store value
  };

  const formMethods = useForm<BookFormValues>({ defaultValues, mode: 'onChange' });
  const selectedStore = useCheckoutStore((state) => state.selectedStore);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const scheduled = useCheckoutStore((state) => state.scheduled);
  const phoneValidated = useCheckoutStore((state) => state.phoneValidated);

  // Set manual mode in the store if it is passed in as a prop
  useLayoutEffect(() => {
    useCheckoutStore.setState({ manual });
  }, [manual]);

  // Set the preselected store if it is passed in as a prop (storeSlug)
  useEffect(() => {
    if (!storeSlug) return;

    let cancelled = false;

    async function loadStore() {
      const client = initApolloClient();

      const { data } = await client.query({
        query: GetStoreDocument,
        variables: { slug: storeSlug ?? '' },
      });

      if (cancelled) return;

      const store = data?.store ?? null;

      useCheckoutStore.setState({
        preSelectedStore: store,
        selectedStore: store,
        selectedSchedule: undefined,
        scheduled: false,
      });
    }

    void loadStore();

    return () => {
      cancelled = true;
    };
  }, [storeSlug]);

  // This component owns whether each step is enabled. Steps stay responsible for their own internal
  // state (eg. the customer step unlocking its name field once the phone number is validated), but
  // they should not decide if a step as a whole is valid.
  const { errors } = useFormState({ control: formMethods.control, name: ['name', 'phone'] });
  const [customerName, phoneNumber] = useWatch({
    control: formMethods.control,
    name: ['name', 'phone'],
  });

  const storeSelected = Boolean(selectedStore);
  const customerValid =
    phoneValidated &&
    Boolean(customerName) &&
    Boolean(phoneNumber) &&
    !errors.name &&
    !errors.phone;

  // When the customer picked "later" we need an actual slot before we can take the payment
  const scheduleValid = !scheduled || Boolean(selectedSchedule);

  // TODO: gate this on the selected services once the services step is implemented
  const scheduleEnabled = storeSelected;
  const customerEnabled = manual ? storeSelected : scheduleEnabled;
  const paymentEnabled = customerEnabled && customerValid && scheduleValid;

  return (
    <FormProvider {...formMethods}>
      <MobileNavbar title="DRYCUT" />

      <Stack gap={10} className="relative">
        <CheckoutIntroText manual={manual} />

        <PassBubble storeSlug={storeSlug} className="-right-8 -top-20 lg:-right-15 lg:-top-15" />

        {selectedStore && (
          <PromotionBadge
            store={selectedStore}
            className="-top-20 -left-8 lg:-top-16 lg:-left-16"
          />
        )}

        <Stack gap={6} className="items-start flex-col lg:flex-row h-full gap-10 lg:gap-6">
          <CheckoutStep
            number={1}
            title={`Salon${!storeSlug ? ' wählen' : ''}`}
            headerHidden={manual}
          >
            <SalonStep manual={manual} />
          </CheckoutStep>

          {!manual && (
            <CheckoutStep number={2} title="Zeit wählen" disabled={!scheduleEnabled}>
              <ScheduleStep />
            </CheckoutStep>
          )}

          <CheckoutStep
            number={manual ? null : 3}
            title={<CustomerDetailsHeading manual={manual} />}
            disabled={!customerEnabled}
          >
            <CustomerDetailsStep />
          </CheckoutStep>

          <CheckoutStep
            number={manual ? null : 4}
            title={<PaymentHeading manual={manual} />}
            disabled={!paymentEnabled}
          >
            <PaymentStep />
          </CheckoutStep>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
